import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // 세션 쿠키를 포함하여 요청
});

// 인증 API
export const authAPI = {
  // 로그인
  login: (password) => {
    return api.post('/auth/login', { password });
  },

  // 로그아웃
  logout: () => {
    return api.post('/auth/logout');
  },

  // 인증 상태 확인
  checkAuth: () => {
    return api.get('/api/auth/check-auth');

  },
};

// 학생 관리 API
export const studentAPI = {
  // 모든 학생 목록 조회
  getStudents: (activeOnly = false) => {
    const params = activeOnly ? { active_only: 'true' } : {};
    return api.get('/students/', { params });
  },

  // 특정 학생 조회
  getStudent: (id) => {
    return api.get(`/students/${id}`);
  },

  // 신규 학생 등록
  createStudent: (studentData) => {
    return api.post('/students/', studentData);
  },

  // 학생 정보 수정
  updateStudent: (id, studentData) => {
    return api.put(`/students/${id}`, studentData);
  },

  // 학생 활성 상태 변경
  updateStudentStatus: (id, isActive) => {
    return api.put(`/students/${id}/status`, { is_active: isActive });
  },

  // 학생 삭제
  deleteStudent: (id) => {
    return api.delete(`/students/${id}`);
  },

  // 학생별 자격증 취득 관련 API (새로운 시스템)
  // 학생의 자격증 취득 목록 조회
  getStudentCertificates: (studentId) => {
    return api.get(`/student-certificates/student/${studentId}`);
  },

  // 학생 자격증 취득 등록
  createStudentCertificate: (studentId, certificateData) => {
    return api.post('/student-certificates/', {
      student_id: studentId,
      ...certificateData
    });
  },

  // 학생 자격증 취득 수정
  updateStudentCertificate: (studentId, certificateId, certificateData) => {
    return api.put(`/student-certificates/${certificateId}`, certificateData);
  },

  // 학생 자격증 취득 삭제
  deleteStudentCertificate: (studentId, certificateId) => {
    return api.delete(`/student-certificates/${certificateId}`);
  },
};

// 수업 관리 API
export const classAPI = {
  // 모든 수업 목록 조회
  getClasses: () => {
    return api.get('/classes/');
  },

  // 특정 수업 조회
  getClass: (id) => {
    return api.get(`/classes/${id}`);
  },

  // 신규 수업 생성
  createClass: (classData) => {
    return api.post('/classes/', classData);
  },

  // 수업 정보 수정
  updateClass: (id, classData) => {
    return api.put(`/classes/${id}`, classData);
  },

  // 수업 삭제
  deleteClass: (id) => {
    return api.delete(`/classes/${id}`);
  },
};

// 시간표 관리 API
export const scheduleAPI = {
  // 전체 시간표 조회
  getSchedules: () => {
    return api.get('/schedules/');
  },

  // 시간표에 학생 배치
  createSchedule: (scheduleData) => {
    return api.post('/schedules/', scheduleData);
  },

  // 시간표 수정
  updateSchedule: (id, scheduleData) => {
    return api.put(`/schedules/${id}`, scheduleData);
  },

  // 시간표에서 학생 제거
  deleteSchedule: (id) => {
    return api.delete(`/schedules/${id}`);
  },

  // 특정 시간대의 빈 좌석 조회
  getAvailableSlots: (dayOfWeek, startTime, duration) => {
    return api.get('/schedules/available', {
      params: {
        day_of_week: dayOfWeek,
        start_time: startTime,
        duration: duration,
      },
    });
  },
};

// 자격증 마스터 관리 API
export const certificateMasterAPI = {
  // 모든 자격증 마스터 목록 조회
  getCertificateMasters: (activeOnly = false) => {
    const params = activeOnly ? { active_only: 'true' } : {};
    return api.get('/certificate-masters/', { params });
  },

  // 특정 자격증 마스터 조회
  getCertificateMaster: (id) => {
    return api.get(`/certificate-masters/${id}`);
  },

  // 신규 자격증 마스터 생성
  createCertificateMaster: (certificateData) => {
    return api.post('/certificate-masters/', certificateData);
  },

  // 자격증 마스터 수정
  updateCertificateMaster: (id, certificateData) => {
    return api.put(`/certificate-masters/${id}`, certificateData);
  },

  // 자격증 마스터 삭제
  deleteCertificateMaster: (id) => {
    return api.delete(`/certificate-masters/${id}`);
  },

  // 자격증 마스터 활성 상태 변경
  updateCertificateMasterStatus: (id, isActive) => {
    return api.put(`/certificate-masters/${id}/status`, { is_active: isActive });
  },
};

// 학생별 자격증 취득 관리 API
export const studentCertificateAPI = {
  // 모든 학생별 자격증 취득 목록 조회
  getStudentCertificates: (studentId = null, certificateId = null) => {
    const params = {};
    if (studentId) params.student_id = studentId;
    if (certificateId) params.certificate_id = certificateId;
    return api.get('/student-certificates/', { params });
  },

  // 특정 학생별 자격증 취득 조회
  getStudentCertificate: (id) => {
    return api.get(`/student-certificates/${id}`);
  },

  // 학생별 자격증 취득 등록
  createStudentCertificate: (certificateData) => {
    return api.post('/student-certificates/', certificateData);
  },

  // 학생별 자격증 취득 수정
  updateStudentCertificate: (id, certificateData) => {
    return api.put(`/student-certificates/${id}`, certificateData);
  },

  // 학생별 자격증 취득 삭제
  deleteStudentCertificate: (id) => {
    return api.delete(`/student-certificates/${id}`);
  },

  // 특정 학생의 자격증 취득 목록 조회
  getStudentCertificatesByStudent: (studentId) => {
    return api.get(`/student-certificates/student/${studentId}`);
  },

  // 특정 자격증의 취득자 목록 조회
  getStudentCertificatesByCertificate: (certificateId) => {
    return api.get(`/student-certificates/certificate/${certificateId}`);
  },
};

// 메모 관리 API
export const memoAPI = {
  // 시간표 메모 조회
  getScheduleMemo: () => {
    return api.get('/schedule-memo');
  },

  // 시간표 메모 저장
  saveScheduleMemo: (memo) => {
    return api.post('/schedule-memo', { memo });
  },
};

export default api;
